/**
 * RankedCellsTable — canvas renderer for the rank_cells_for_application
 * tool output (`artifact_type: 'ranked_cells_table'`).
 *
 * Backend output shape (see F1-PLAN §5.2.1):
 *
 *   {
 *     artifact_type: 'ranked_cells_table',
 *     branch_label?: string,                  // e.g. 'Power Tool'
 *     rows: RankedCell[],                     // sorted high → low by score
 *     plot_data?: ScatterPlotData,            // Ragone for Plot tab
 *     confidence: number,                     // 0..1
 *   }
 *
 *   RankedCell = {
 *     manufacturer: string,
 *     name: string,
 *     format?: string,                        // cell columns of MS's table —
 *     c_n_ah?: number,                        //   present only where the flow
 *     v_mean_v?: number,                      //   produced them (CELL_COLUMNS)
 *     low_temp_ratio?: number,
 *     score: number,                          // min-max: 0..1 across the
 *                                             //   SURVIVORS, so a ruled-out
 *                                             //   cell may fall outside it
 *     constraint_pass: bool,                  // all hard constraints met?
 *     scoring_incomplete?: true,              // a weighted figure is unmeasured
 *     reason: string,                         // "Highest p_cont_grav in cohort"
 *     weak_dimensions: string[],              // ["mass_g"], optional
 *     pack?: { … },                           // pack figures (PACK_COLUMNS)
 *   }
 *
 * COLUMN COVERAGE (2026-09-09). The table carries every column of MS's
 * `result_table` — the four cell figures above plus the pack block — because
 * they were chosen deliberately and a subset is not the result. Both groups
 * render by PRESENCE, so a flow that sizes no pack still renders narrow.
 *
 * Visual goals (F1-PLAN §6.4):
 *   - Top-N table with score bar + percentage
 *   - Constraint-pass icon (✓ green / ✗ red) per row
 *   - Click row → open cell detail in new tab (manufacturer+name → slug)
 *
 * A REJECTED CELL IS NOT A RESULT (2026-09-04). The table lists only cells that
 * meet every hard constraint; the rest move to the collapsed "ruled out" block
 * below it, with the shortfall the backend computed and no rank or score.
 *
 * This restores parity with MS's reference workflow, which hard-filters at
 * Section 2 (`idx_after_req = idx_after_fund(idx_req)` in
 * `reference/matlab/*_cell_selection.m`) and scores only the survivors — a cell
 * that misses a requirement can never enter its `result_table`, and when none
 * survive the script errors with "No cells meet the requirements. Consider: …"
 * rather than ranking anything. The one place it keeps non-passers is the grey
 * background layer of the Ragone scatter, never the table.
 *
 * Failures stay REACHABLE (the "5 W more continuous power and this cell fits"
 * conversation) but stop being presented as recommendations. Before this, the
 * client-side score sort below interleaved them with the passers and `idx + 1`
 * handed them a rank the backend had explicitly withheld — so the top three
 * rows of a power-tool ranking were cells that miss peak power, at 100%.
 */

import { useLayoutEffect, useRef, useState } from 'preact/hooks'
import { generateSlugFromCell } from '../../../utils/cellSlugMap.js'
import { manualPicks, toggleManualPick } from '../../../state/chatStore.js'

/** Stable per-row identity — matches the manualPicks pointId shape elsewhere
 *  in this file, and is what the <tr> key + the FLIP ref map are keyed by. */
function rowId(row) {
  return `${row.manufacturer}::${row.name}`
}

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

/**
 * FLIP (First-Last-Invert-Play) reorder animation for the table rows. Because
 * the <tr> key is now the index-free `rowId` (see below), Preact keeps the
 * same DOM node for a cell across a re-rank even as its rank/position
 * changes — a precondition for FLIP. On every render where the row ORDER
 * changed, this measures each row's new top, diffs it against the top
 * recorded after the PREVIOUS layout effect (which is exactly the visual
 * position just before this update, since nothing paints in between), and
 * animates the delta away with a transform transition.
 *
 * Reduced-motion: still refreshes the position cache (so a later animation
 * starts from a correct baseline) but never sets a transform.
 */
function useRowFlip(orderKey) {
  const rowRefs = useRef(new Map())
  const prevTopsRef = useRef(new Map())

  const setRowRef = (id) => (el) => {
    if (el) rowRefs.current.set(id, el)
    else rowRefs.current.delete(id)
  }

  useLayoutEffect(() => {
    const reduceMotion = prefersReducedMotion()
    const prevTops = prevTopsRef.current
    const nextTops = new Map()

    rowRefs.current.forEach((el, id) => {
      const top = el.getBoundingClientRect().top
      nextTops.set(id, top)
      if (reduceMotion) return
      const prevTop = prevTops.get(id)
      if (prevTop == null) return
      const delta = prevTop - top
      if (Math.abs(delta) < 1) return
      // Invert: jump to where it visually was, with no transition…
      el.style.transition = 'none'
      el.style.transform = `translateY(${delta}px)`
      // …force a reflow so the browser commits the jump before the next line…
      // eslint-disable-next-line no-unused-expressions
      el.offsetHeight
      // …then play: transition back to its real (new) position.
      el.style.transition = 'transform var(--ion-dur-lg, 420ms) var(--ion-ease-smooth, cubic-bezier(0.22, 1, 0.36, 1))'
      el.style.transform = ''
      const clearTransition = () => {
        el.style.transition = ''
        el.removeEventListener('transitionend', clearTransition)
      }
      el.addEventListener('transitionend', clearTransition)
    })

    prevTopsRef.current = nextTops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderKey])

  return setRowRef
}

function pct(score) {
  if (!Number.isFinite(score)) return 0
  return Math.round(Math.max(0, Math.min(1, score)) * 100)
}

/** The score as TEXT. Under min-max the 0..1 scale is set by the cells that
 *  passed every requirement, so a cell that missed one can legitimately land
 *  below 0 or above 1. The bar clamps (a bar cannot be -30 % long); the label
 *  must not silently clamp too, or a cell scoring far below the field reads as
 *  a clean 0 %. */
function pctLabel(score) {
  if (!Number.isFinite(score)) return '—'
  if (score < 0) return '<0%'
  if (score > 1) return '>100%'
  return `${pct(score)}%`
}

/**
 * Sort merged rows by a column. Pure — exported for tests. Manual-add rows have
 * a null score and always sort to the end of a score sort. 'name' sorts by
 * "manufacturer name"; 'pass' by constraint pass. Default: score desc.
 */
export function sortRows(rows, key = 'score', dir = 'desc') {
  const safe = Array.isArray(rows) ? [...rows] : []
  const sign = dir === 'asc' ? 1 : -1
  return safe.sort((a, b) => {
    if (key === 'name') {
      return sign * `${a.manufacturer} ${a.name}`.localeCompare(`${b.manufacturer} ${b.name}`)
    }
    if (key === 'pass') {
      return sign * ((a.constraint_pass === false ? 0 : 1) - (b.constraint_pass === false ? 0 : 1))
    }
    // score — non-finite (manual rows) sink to the bottom regardless of dir.
    const av = Number.isFinite(a.score) ? a.score : -Infinity
    const bv = Number.isFinite(b.score) ? b.score : -Infinity
    return sign * (av - bv)
  })
}

/**
 * Merge the user's manual picks into the backend ranked rows. A pick already
 * present (by manufacturer::name) is left as-is; the rest are appended as
 * "Added by you" rows (carrying their original ruled-out state). Pure —
 * exported for tests.
 */
export function mergeManualPicks(rows, picks) {
  const safeRows = Array.isArray(rows) ? rows : []
  const existing = new Set(safeRows.map((r) => `${r.manufacturer}::${r.name}`))
  const extras = (Array.isArray(picks) ? picks : [])
    .filter((p) => p && p.pointId && !existing.has(p.pointId))
    .map((p) => ({
      manufacturer: p.manufacturer,
      name: p.name,
      score: null,
      constraint_pass: p.originalPassed !== false,
      reason: p.originalPassed === false ? 'Added by you — was ruled out' : 'Added by you',
      weak_dimensions: [],
      manual: true,
    }))
  return [...safeRows, ...extras]
}

/**
 * Split the merged rows into what the table may show and what it may not.
 *
 * `results` are the cells that meet every hard constraint — the only thing MS's
 * script would call a result. A MANUAL pick is always a result even when it was
 * ruled out: the user asked for it explicitly, so it stays in the table with its
 * honest ✗ rather than being silently swept into the rejects.
 *
 * `nearMisses` keeps the incoming order (the backend already sorted score-desc),
 * so the closest misses read first. Pure — exported for tests.
 */
export function splitByConstraint(rows) {
  const safe = Array.isArray(rows) ? rows : []
  const results = []
  const nearMisses = []
  for (const row of safe) {
    if (row && (row.constraint_pass !== false || row.manual)) results.push(row)
    else nearMisses.push(row)
  }
  return { results, nearMisses }
}

/**
 * Pack columns, in MS's result-table order. Rendered only when the backend
 * actually sized a pack (a flow with system requirements) — outside one there
 * is no topology and the columns would be six dashes.
 *
 * Range and runtime are mutually exclusive in practice: an eBike flow supplies
 * a consumption figure, a power-tool flow a continuous load, so exactly one of
 * them arrives. Deciding by PRESENCE rather than by branch keeps this table
 * ignorant of which flows exist.
 */
const PACK_COLUMNS = [
  { key: 'topology', head: 'Pack', fmt: (v) => v },
  { key: 'pack_voltage_v', head: 'Voltage', unit: 'V', digits: 1 },
  { key: 'pack_capacity_ah', head: 'Capacity', unit: 'Ah', digits: 2 },
  { key: 'pack_energy_wh', head: 'Energy', unit: 'Wh', digits: 1 },
  { key: 'pack_peak_power_w', head: 'Peak power', unit: 'W', digits: 0 },
  { key: 'pack_cont_power_w', head: 'Cont. power', unit: 'W', digits: 0 },
  { key: 'pack_range_km', head: 'Range', unit: 'km', digits: 0 },
  { key: 'pack_runtime_min', head: 'Runtime', unit: 'min', digits: 0 },
  { key: 'pack_mass_kg', head: 'Mass', unit: 'kg', digits: 2 },
  { key: 'pack_volume_l', head: 'Volume', unit: 'l', digits: 2 },
]

/** The CELL-level columns of MS's result table — format, nominal capacity, mean
 *  voltage, cold-weather ratio. They sit between `Cell` and the pack block, in
 *  his order, and follow the same presence rule as the pack columns: a flow
 *  that produced none of them renders none of them.
 *
 *  These arrive FLATTENED on the row (not under `pack`) because they describe
 *  the cell, not the pack it would build. */
const CELL_COLUMNS = [
  { key: 'format', head: 'Format', fmt: (v) => v },
  // "C nom", not "Capacity" — the pack block has a Capacity (Ah) column too,
  // and two identically-headed Ah columns in one table is a reading error
  // waiting to happen.
  { key: 'c_n_ah', head: 'C nom', unit: 'Ah', digits: 2 },
  { key: 'v_mean_v', head: 'V mean', unit: 'V', digits: 2 },
  { key: 'low_temp_ratio', head: 'Low temp', digits: 3 },
]

export function cellColumnsFor(rows) {
  const safe = Array.isArray(rows) ? rows : []
  return CELL_COLUMNS.filter((c) => safe.some((r) => r && r[c.key] != null)).map((c) => ({
    key: c.key,
    head: c.unit ? `${c.head} (${c.unit})` : c.head,
    // Numbers right-align, the one text column doesn't.
    text: c.digits == null,
    format: (row) => {
      const v = row && row[c.key]
      if (v == null) return '—'
      return c.fmt ? c.fmt(v) : Number(v).toFixed(c.digits)
    },
  }))
}

export function packColumnsFor(rows) {
  const safe = Array.isArray(rows) ? rows : []
  // A column earns its place only if at least one row can fill it — a table of
  // dashes is worse than no column.
  return PACK_COLUMNS.filter((c) =>
    safe.some((r) => r && r.pack && r.pack[c.key] != null),
  ).map((c) => ({
    key: c.key,
    head: c.unit ? `${c.head} (${c.unit})` : c.head,
    format: (pack) => {
      const v = pack && pack[c.key]
      if (v == null) return '—'
      return c.fmt ? c.fmt(v) : Number(v).toFixed(c.digits)
    },
  }))
}

/** One line naming the constants every pack figure above was computed with, so
 *  an admin-set value cannot shape the answer invisibly. */
export function packAssumptionsLine(a) {
  if (!a || typeof a !== 'object') return null
  const parts = []
  if (a.peak_power_derating != null) parts.push(`peak derating ${a.peak_power_derating}`)
  if (a.cont_power_derating != null) parts.push(`continuous derating ${a.cont_power_derating}`)
  if (a.weight_efficiency != null) {
    parts.push(`pack weight ${Math.round(a.weight_efficiency * 100)}% cells`)
  }
  if (a.volume_efficiency != null) {
    parts.push(`pack volume ${Math.round(a.volume_efficiency * 100)}% cells`)
  }
  if (a.energy_basis === 'w_cont') parts.push('energy at continuous load')
  return parts.length ? parts.join(' · ') : null
}

function openCellDetail(cell) {
  const slug = generateSlugFromCell({ manufacturer: cell.manufacturer, name: cell.name })
  if (!slug) {
    console.warn('[RankedCellsTable] cannot derive slug for', cell)
    return
  }
  // New tab so the user keeps their conversation context intact — mirrors the
  // SourceHighlight chip behaviour elsewhere in Ion. Cell detail lives on the
  // /explorer query-param route; the path-style /<slug>/<section> form 404s.
  window.open(`/explorer?cell=${slug}&section=factsheet`, '_blank', 'noopener,noreferrer')
}

/**
 * @param {object} props
 * @param {object} props.data
 */
export function RankedCellsTable({ data }) {
  const [sort, setSort] = useState({ key: 'score', dir: 'desc' })
  const picks = manualPicks.value

  // Merge the user's manual picks (incl. ones Ion ruled out) into the shortlist,
  // then apply the active column sort. Computed before the `!data` early
  // return so the FLIP hook below always runs in the same order (Rules of
  // Hooks) — falls back to [] when there's nothing to render yet.
  // Rejects are split off BEFORE the column sort, so no sort direction can ever
  // lift one back above a passer — which is exactly how they reached #1.
  const { results, nearMisses } = splitByConstraint(
    data ? mergeManualPicks(data.rows, picks) : [],
  )
  const rows = sortRows(results, sort.key, sort.dir)
  // Re-run the FLIP measurement whenever the row ORDER changes (a re-rank),
  // not on every render (e.g. a favorite toggle that doesn't reorder).
  const setRowRef = useRowFlip(rows.map(rowId).join('|'))

  if (!data) return null

  const favSet = new Set(picks.map((p) => p.pointId))
  const branchLabel = data.branch_label || null
  const packCols = packColumnsFor(rows)
  const cellCols = cellColumnsFor(rows)
  const assumptionsLine = packAssumptionsLine(data.pack_assumptions)

  // Click a header to sort by it; click again to flip direction. Name defaults
  // to ascending, the numeric/boolean columns to descending.
  const toggleSort = (key) =>
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'name' ? 'asc' : 'desc' },
    )
  const sortArrow = (key) => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '')

  const toggleFavorite = (row) =>
    toggleManualPick({
      pointId: `${row.manufacturer}::${row.name}`,
      manufacturer: row.manufacturer,
      name: row.name,
      originalPassed: row.constraint_pass !== false,
      exclusionReason: row.constraint_pass === false ? 'ruled out by your requirements' : null,
      pinnedAt: Date.now(),
    })

  // Nothing at all came back — not even a reject to explain.
  if (rows.length === 0 && nearMisses.length === 0) {
    return (
      <div class="ion-ranked-cells ion-ranked-cells--empty">
        <p>No cells matched the hard constraints. Try relaxing one of them.</p>
      </div>
    )
  }

  return (
    <article class="ion-ranked-cells" aria-label="Ranked cells">
      <header class="ion-ranked-cells__header">
        <div class="ion-ranked-cells__title-block">
          <h3 class="ion-ranked-cells__title">
            {rows.length === 0
              ? `No cells meet the requirements${branchLabel ? ` for ${branchLabel}` : ''}`
              : `Top ${rows.length} ${branchLabel ? `for ${branchLabel}` : 'cells'}`}
          </h3>
          <p class="ion-ranked-cells__subtitle">
            {rows.length > 0 && (
              <span>
                {rows.length} {rows.length === 1 ? 'cell meets' : 'cells meet'} every requirement
              </span>
            )}
            {rows.length > 0 && nearMisses.length > 0 && ' · '}
            {nearMisses.length > 0 && <span>{nearMisses.length} ruled out</span>}
          </p>
        </div>
      </header>

      {rows.length === 0 && (
        <p class="ion-ranked-cells__empty-note">
          No cell meets every requirement. The closest misses are below, with what
          each one would need.
        </p>
      )}

      {rows.length > 0 && (
      <div class="ion-ranked-cells__scroll">
        <table class="ion-ranked-cells__table">
          <thead>
            <tr>
              <th scope="col" class="ion-ranked-cells__fav-col" aria-label="Favorite" />
              <th scope="col" class="ion-ranked-cells__rank-col">#</th>
              <th
                scope="col"
                class="ion-ranked-cells__th-sort"
                aria-sort={sort.key === 'name' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                onClick={() => toggleSort('name')}
              >
                Cell{sortArrow('name')}
              </th>
              {cellCols.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  class={`ion-ranked-cells__cell-col${c.text ? ' ion-ranked-cells__cell-col--text' : ''}`}
                >
                  {c.head}
                </th>
              ))}
              <th
                scope="col"
                class="ion-ranked-cells__score-col ion-ranked-cells__th-sort"
                aria-sort={sort.key === 'score' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                onClick={() => toggleSort('score')}
              >
                Score{sortArrow('score')}
              </th>
              <th
                scope="col"
                class="ion-ranked-cells__pass-col ion-ranked-cells__th-sort"
                aria-sort={sort.key === 'pass' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                onClick={() => toggleSort('pass')}
              >
                Pass{sortArrow('pass')}
              </th>
              {packCols.map((c) => (
                <th key={c.key} scope="col" class="ion-ranked-cells__pack-col">
                  {c.head}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const passed = row.constraint_pass !== false
              const id = rowId(row)
              return (
                <tr
                  key={id}
                  ref={setRowRef(id)}
                  class="ion-ranked-cells__row"
                  tabIndex={0}
                  role="button"
                  aria-label={`Open ${row.manufacturer} ${row.name} detail`}
                  onClick={() => openCellDetail(row)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      openCellDetail(row)
                    }
                  }}
                >
                  <td class="ion-ranked-cells__fav">
                    {(() => {
                      const fav = favSet.has(`${row.manufacturer}::${row.name}`)
                      return (
                        <button
                          type="button"
                          class={`ion-ranked-cells__fav-btn${fav ? ' ion-ranked-cells__fav-btn--on' : ''}`}
                          aria-pressed={fav}
                          aria-label={fav ? 'Remove from your picks' : 'Add to your picks'}
                          title={fav ? 'Remove from your picks' : 'Add to your picks'}
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleFavorite(row)
                          }}
                        >
                          {fav ? '★' : '☆'}
                        </button>
                      )
                    })()}
                  </td>
                  <td class="ion-ranked-cells__rank">
                    {row.manual ? (
                      <span class="ion-ranked-cells__rank-pin" aria-label="Added by you">+</span>
                    ) : row.rank != null ? (
                      row.rank
                    ) : row.scoring_incomplete ? (
                      // The backend withheld the rank because one of the
                      // weighted figures is unmeasured for this cell — MS's
                      // script refuses to rank at all in that case. Handing it
                      // `idx + 1` would restore exactly the rank it withheld.
                      <span
                        class="ion-ranked-cells__rank-unranked"
                        title="Not ranked — a weighted figure is unmeasured for this cell"
                      >
                        —
                      </span>
                    ) : (
                      // Position is the fallback, never an override.
                      idx + 1
                    )}
                  </td>
                  <td class="ion-ranked-cells__cell">
                    <span class="ion-ranked-cells__manufacturer">{row.manufacturer}</span>
                    <span class="ion-ranked-cells__name">{row.name}</span>
                    {row.manual && (
                      <span class="ion-ranked-cells__manual-badge">Added by you</span>
                    )}
                  </td>
                  {cellCols.map((c) => (
                    <td
                      key={c.key}
                      class={`ion-ranked-cells__cellfig${c.text ? ' ion-ranked-cells__cellfig--text' : ''}`}
                    >
                      {c.format(row)}
                    </td>
                  ))}
                  <td class="ion-ranked-cells__score">
                    {row.manual ? (
                      <span class="ion-ranked-cells__score-pct">—</span>
                    ) : (
                      <div class="ion-ranked-cells__score-inner">
                        <span class="ion-ranked-cells__score-bar" aria-hidden="true">
                          <span
                            class="ion-ranked-cells__score-fill"
                            style={`width: ${pct(row.score)}%`}
                          />
                        </span>
                        <span class="ion-ranked-cells__score-pct">{pctLabel(row.score)}</span>
                      </div>
                    )}
                  </td>
                  <td class={`ion-ranked-cells__pass ion-ranked-cells__pass--${passed ? 'ok' : 'fail'}`}>
                    <span aria-label={passed ? 'All constraints met' : 'One or more constraints missed'}>
                      {passed ? '✓' : '✗'}
                    </span>
                  </td>
                  {packCols.map((c) => (
                    <td key={c.key} class="ion-ranked-cells__pack">
                      {c.format(row.pack)}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      )}

      {nearMisses.length > 0 && (
        <details class="ion-ranked-cells__misses">
          <summary class="ion-ranked-cells__misses-summary">
            {nearMisses.length} {nearMisses.length === 1 ? 'cell was' : 'cells were'} ruled out by
            your requirements
          </summary>
          <ul class="ion-ranked-cells__misses-list">
            {nearMisses.map((row) => (
              <li key={rowId(row)} class="ion-ranked-cells__miss">
                <button
                  type="button"
                  class="ion-ranked-cells__miss-cell"
                  onClick={() => openCellDetail(row)}
                >
                  <span class="ion-ranked-cells__manufacturer">{row.manufacturer}</span>{' '}
                  <span class="ion-ranked-cells__name">{row.name}</span>
                </button>
                {/* The shortfall, in the backend's own words ("peak power
                    861.115 W < 1000 W needed") — the whole point of keeping a
                    reject visible. No score, no rank: it is not a result. */}
                <span class="ion-ranked-cells__miss-reason">
                  {row.reason || 'Misses a hard constraint'}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {(assumptionsLine || Number.isFinite(data.ranked_total)) && (
        <footer class="ion-ranked-cells__foot">
          {Number.isFinite(data.ranked_total) &&
            data.ranked_total > rows.length + nearMisses.length && (
              <span class="ion-ranked-cells__foot-item">
                Showing {rows.length + nearMisses.length} of {data.ranked_total} ranked cells
              </span>
            )}
          {assumptionsLine && (
            <span class="ion-ranked-cells__foot-item" title="Pack sizing assumptions">
              Pack assumptions: {assumptionsLine}
            </span>
          )}
        </footer>
      )}
    </article>
  )
}

// Exported for tests — pure helpers, no JSX coupling.
export const __test__ = {
  pct,
  pctLabel,
  mergeManualPicks,
  sortRows,
  splitByConstraint,
}
